import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Inbox, Search, Filter, MoreHorizontal, Clock, User, Tag, MessageSquare,
  CheckCircle, AlertCircle, Circle, ArrowRight, ChevronDown, X, RefreshCw,
  Hash, Mail, Globe, Zap, Eye, Users as UsersIcon, Download, ChevronLeft,
  ChevronRight as ChevronRightIcon, Columns, Square, CheckSquare, Trash2,
  UserPlus, Archive, Loader2, Save, BookmarkCheck, Star, Plus, Timer, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { listTickets, subscribe, isAhaComplete } from '../data/demoStore';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuCheckboxItem,
  DropdownMenuLabel
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const mockTickets = [
  {
    id: 'TKT-1234',
    subject: 'Need access to Salesforce',
    requester: { name: 'Sarah Chen', email: 'sarah@company.com', avatar: 'SC' },
    channel: { type: 'slack', name: '#it-help' },
    category: 'access',
    priority: 'medium',
    status: 'open',
    assignedTo: { type: 'team', name: 'Identity & Access' },
    createdAt: '2024-01-15T10:30:00Z',
    lastActivity: '5 min ago',
    messageCount: 3,
    aiSuggestion: 'Standard access request - workflow available'
  },
  {
    id: 'TKT-1235',
    subject: 'Password reset not working',
    requester: { name: 'Mike Torres', email: 'mike@company.com', avatar: 'MT' },
    channel: { type: 'email', name: 'it-support@company.com' },
    category: 'password',
    priority: 'high',
    status: 'in_progress',
    assignedTo: { type: 'agent', name: 'Emily Zhang' },
    createdAt: '2024-01-15T09:15:00Z',
    lastActivity: '2 min ago',
    messageCount: 5,
    aiSuggestion: 'User may be locked out - check Okta status'
  },
  {
    id: 'TKT-1236',
    subject: 'New laptop request for new hire',
    requester: { name: 'Jordan Lee', email: 'jordan@company.com', avatar: 'JL' },
    channel: { type: 'slack', name: '#it-help' },
    category: 'hardware',
    priority: 'medium',
    status: 'pending_approval',
    assignedTo: { type: 'team', name: 'Endpoint Management' },
    createdAt: '2024-01-15T08:45:00Z',
    lastActivity: '1 hour ago',
    messageCount: 2,
    aiSuggestion: 'Requires manager approval - hardware budget'
  },
  {
    id: 'TKT-1237',
    subject: 'Suspicious login alert - John Doe account',
    requester: { name: 'Security Bot', email: 'security@company.com', avatar: 'SB' },
    channel: { type: 'webhook', name: 'CrowdStrike' },
    category: 'security',
    priority: 'critical',
    status: 'open',
    assignedTo: { type: 'team', name: 'Security' },
    createdAt: '2024-01-15T10:45:00Z',
    lastActivity: '1 min ago',
    messageCount: 1,
    aiSuggestion: 'Auto-contained: session terminated, awaiting review'
  },
  {
    id: 'TKT-1238',
    subject: 'VPN connection issues from home',
    requester: { name: 'Alex Kim', email: 'alex@company.com', avatar: 'AK' },
    channel: { type: 'portal', name: 'Self-Service Portal' },
    category: 'network',
    priority: 'low',
    status: 'resolved',
    assignedTo: { type: 'workflow', name: 'VPN Troubleshooter' },
    createdAt: '2024-01-15T07:30:00Z',
    lastActivity: '3 hours ago',
    messageCount: 4,
    aiSuggestion: 'Resolved automatically via guided troubleshooting'
  },
  {
    id: 'TKT-1239',
    subject: 'Request to add user to GitHub org',
    requester: { name: 'Taylor Brown', email: 'taylor@company.com', avatar: 'TB' },
    channel: { type: 'slack', name: '#access-requests' },
    category: 'access',
    priority: 'medium',
    status: 'in_progress',
    assignedTo: { type: 'workflow', name: 'GitHub Access' },
    createdAt: '2024-01-15T09:00:00Z',
    lastActivity: '30 min ago',
    messageCount: 2,
    aiSuggestion: 'Executing: awaiting GitHub API confirmation'
  }
];

const statusConfig = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Circle },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: RefreshCw },
  pending_approval: { label: 'Pending Approval', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-800 border-slate-200', icon: CheckCircle }
};

const priorityConfig = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-800 border-red-200' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600 border-slate-200' }
};

const channelIcons = {
  slack: Hash,
  teams: MessageSquare,
  email: Mail,
  portal: Globe,
  webhook: Zap
};

const categories = ['all', 'access', 'password', 'hardware', 'security', 'network'];
const statuses = ['all', 'open', 'in_progress', 'pending_approval', 'resolved'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const savedViews = [
  { id: 'all', name: 'All Tickets', filters: {}, isDefault: true },
  { id: 'my-open', name: 'My Open Tickets', filters: { status: 'open', assignedTo: 'me' }, icon: Star },
  { id: 'critical', name: 'Critical Priority', filters: { priority: 'critical' }, icon: AlertCircle },
  { id: 'unassigned', name: 'Unassigned', filters: { assignedTo: 'unassigned' }, icon: Inbox },
  { id: 'sla-breaching', name: 'SLA Breaching', filters: { slaBreach: true }, icon: Timer },
  { id: 'pending-approval', name: 'Pending Approval', filters: { status: 'pending_approval' }, icon: Clock },
];

const getSLAStatus = (ticket) => {
  const createdAt = new Date(ticket.createdAt);
  const now = new Date();
  const hoursOpen = (now - createdAt) / (1000 * 60 * 60);

  const slaThresholds = {
    critical: 1,
    high: 4,
    medium: 8,
    low: 24
  };

  const threshold = slaThresholds[ticket.priority] || 8;
  const percentUsed = (hoursOpen / threshold) * 100;

  if (percentUsed >= 100) return { status: 'breached', label: 'SLA Breached', color: 'text-red-600 bg-red-100' };
  if (percentUsed >= 75) return { status: 'at-risk', label: 'At Risk', color: 'text-amber-600 bg-amber-100' };
  return { status: 'on-track', label: 'On Track', color: 'text-green-600 bg-green-100' };
};

const defaultColumns = {
  id: true,
  priority: true,
  status: true,
  subject: true,
  requester: true,
  channel: true,
  assignedTo: true,
  lastActivity: true,
  aiSuggestion: true
};

export const Tickets = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get('status') || 'all');
  const [selectedPriority, setSelectedPriority] = useState(searchParams.get('priority') || 'all');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('size') || '10', 10));
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [visibleColumns, setVisibleColumns] = useState(defaultColumns);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentView, setCurrentView] = useState('all');
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  // Demo store integration
  const [demoTickets, setDemoTickets] = useState(listTickets());
  const [aha, setAha] = useState(isAhaComplete());
  const [showAdvanced, setShowAdvanced] = useState(() => {
    try {
      return localStorage.getItem('vedaya_tickets_advanced') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    return subscribe(() => {
      setDemoTickets(listTickets());
      setAha(isAhaComplete());
    });
  }, []);

  const advanced = aha && showAdvanced;

  const toggleAdvanced = () => {
    const next = !showAdvanced;
    try {
      localStorage.setItem('vedaya_tickets_advanced', next ? 'true' : 'false');
    } catch {}
    setShowAdvanced(next);
  };

  const filteredTickets = useMemo(() => {
    return mockTickets.filter(ticket => {
      const matchesSearch = ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.requester.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.id.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || ticket.category === selectedCategory;
      const matchesStatus = selectedStatus === 'all' || ticket.status === selectedStatus;
      const matchesPriority = selectedPriority === 'all' || ticket.priority === selectedPriority;
      return matchesSearch && matchesCategory && matchesStatus && matchesPriority;
    });
  }, [searchQuery, selectedCategory, selectedStatus, selectedPriority]);

  const totalPages = Math.ceil(filteredTickets.length / pageSize);
  const paginatedTickets = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTickets.slice(start, start + pageSize);
  }, [filteredTickets, currentPage, pageSize]);

  const slaStats = useMemo(() => {
    const openTickets = mockTickets.filter(t => t.status !== 'resolved' && t.status !== 'closed');
    const breached = openTickets.filter(t => getSLAStatus(t).status === 'breached').length;
    const atRisk = openTickets.filter(t => getSLAStatus(t).status === 'at-risk').length;
    return { breached, atRisk, total: openTickets.length };
  }, []);

  const stats = {
    total: mockTickets.length,
    open: mockTickets.filter(t => t.status === 'open').length,
    inProgress: mockTickets.filter(t => t.status === 'in_progress').length,
    critical: mockTickets.filter(t => t.priority === 'critical').length,
    slaBreached: slaStats.breached,
    slaAtRisk: slaStats.atRisk
  };

  const updateUrlParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '1' && value !== '10') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedStatus('all');
    setSelectedPriority('all');
    setSearchQuery('');
    setCurrentPage(1);
    setSelectedTickets(new Set());
    setSearchParams(new URLSearchParams());
  };

  const hasFilters = selectedCategory !== 'all' || selectedStatus !== 'all' ||
    selectedPriority !== 'all' || searchQuery !== '';

  const handleSelectAllPage = () => {
    if (selectedTickets.size === paginatedTickets.length && !selectAllMatching) {
      setSelectedTickets(new Set());
      setSelectAllMatching(false);
    } else {
      setSelectedTickets(new Set(paginatedTickets.map(t => t.id)));
      setSelectAllMatching(false);
    }
  };

  const handleSelectAllMatching = () => {
    setSelectedTickets(new Set(filteredTickets.map(t => t.id)));
    setSelectAllMatching(true);
    toast.success(`Selected all ${filteredTickets.length} matching tickets`);
  };

  const handleViewChange = (viewId) => {
    setCurrentView(viewId);
    const view = savedViews.find(v => v.id === viewId);
    if (view) {
      if (view.filters.status) setSelectedStatus(view.filters.status);
      else setSelectedStatus('all');
      if (view.filters.priority) setSelectedPriority(view.filters.priority);
      else setSelectedPriority('all');
      setCurrentPage(1);
      setSelectedTickets(new Set());
      setSelectAllMatching(false);
    }
  };

  const handleSaveCurrentView = () => {
    toast.success('View saved', { description: 'Your current filters have been saved as a new view.' });
  };

  const handleSelectTicket = (ticketId, e) => {
    e.stopPropagation();
    const newSelection = new Set(selectedTickets);
    if (newSelection.has(ticketId)) {
      newSelection.delete(ticketId);
    } else {
      newSelection.add(ticketId);
    }
    setSelectedTickets(newSelection);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsRefreshing(false);
    toast.success('Tickets refreshed');
  };

  const handleExportCSV = () => {
    const headers = ['ID', 'Subject', 'Requester', 'Priority', 'Status', 'Category', 'Channel', 'Assigned To', 'Last Activity'];
    const ticketsToExport = selectedTickets.size > 0
      ? filteredTickets.filter(t => selectedTickets.has(t.id))
      : filteredTickets;

    const rows = ticketsToExport.map(t => [
      t.id,
      `"${t.subject}"`,
      t.requester.name,
      t.priority,
      t.status,
      t.category,
      t.channel.name,
      t.assignedTo.name,
      t.lastActivity
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tickets-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${ticketsToExport.length} tickets`);
  };

  const handleBulkAssign = () => {
    toast.info(`Assign ${selectedTickets.size} tickets`, { description: 'This would open an assignment dialog' });
  };

  const handleBulkClose = () => {
    toast.info(`Close ${selectedTickets.size} tickets`, { description: 'This would close selected tickets' });
    setSelectedTickets(new Set());
  };

  const toggleColumn = (col) => {
    setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }));
  };

  // Queue tickets from demo store (open + pending_approval only)
  const queueTickets = useMemo(() => {
    return demoTickets.filter(t => t.status === 'open' || t.status === 'pending_approval');
  }, [demoTickets]);

  // Simple queue mode UI (pre-advanced)
  if (!advanced) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Inbox className="w-5 h-5 text-muted-foreground" />
              Cases
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {queueTickets.length} open {queueTickets.length === 1 ? 'case' : 'cases'}
            </p>
          </div>
          {aha && (
            <Button variant="outline" size="sm" onClick={toggleAdvanced}>
              <Eye className="w-4 h-4 mr-2" />
              Show advanced view
            </Button>
          )}
        </div>

        {queueTickets.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">All caught up!</h3>
              <p className="text-sm text-muted-foreground">No open cases right now.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {queueTickets.map((ticket) => {
              const status = statusConfig[ticket.status] || statusConfig.open;
              const StatusIcon = status.icon;
              return (
                <Card key={ticket.id} className="hover:border-primary/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-xs font-medium shrink-0">
                          {ticket.requester?.avatar || 'U'}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-muted-foreground">{ticket.id}</span>
                            <Badge variant="outline" className={cn("text-xs", status.color)}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {status.label}
                            </Badge>
                          </div>
                          <h3 className="text-sm font-medium text-foreground truncate">{ticket.subject}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {ticket.requester?.name || 'Unknown'} · {ticket.lastActivity || 'Just now'}
                          </p>
                        </div>
                      </div>
                      <Button size="sm" onClick={() => navigate(`/tickets/${ticket.id}`)}>
                        Open
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Tickets</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Incoming requests from all channels
              </p>
            </div>
            <div className="flex items-center gap-2">
              {aha && (
                <Button variant="ghost" size="sm" onClick={toggleAdvanced}>
                  <Eye className="w-4 h-4 mr-2" />
                  Hide advanced
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <BookmarkCheck className="w-4 h-4 mr-2" />
                    {savedViews.find(v => v.id === currentView)?.name || 'Views'}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Saved Views</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {savedViews.map(view => {
                    const ViewIcon = view.icon || BookmarkCheck;
                    return (
                      <DropdownMenuItem
                        key={view.id}
                        onClick={() => handleViewChange(view.id)}
                        className={cn(currentView === view.id && "bg-accent")}
                      >
                        <ViewIcon className="w-4 h-4 mr-2" />
                        {view.name}
                        {view.isDefault && <Badge variant="secondary" className="ml-auto text-xs">Default</Badge>}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSaveCurrentView}>
                    <Plus className="w-4 h-4 mr-2" />
                    Save Current View
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns className="w-4 h-4 mr-2" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {Object.entries(defaultColumns).map(([col]) => (
                    <DropdownMenuCheckboxItem
                      key={col}
                      checked={visibleColumns[col]}
                      onCheckedChange={() => toggleColumn(col)}
                      className="capitalize"
                    >
                      {col.replace(/([A-Z])/g, ' $1').trim()}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                {isRefreshing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Inbox className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total Tickets</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.open}</p>
                  <p className="text-xs text-muted-foreground">Open</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.inProgress}</p>
                  <p className="text-xs text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.critical}</p>
                  <p className="text-xs text-muted-foreground">Critical</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={cn(stats.slaBreached > 0 && "border-red-200 bg-red-50/50")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center",
                  stats.slaBreached > 0 ? "bg-red-100" : "bg-green-100"
                )}>
                  <Timer className={cn(
                    "w-5 h-5",
                    stats.slaBreached > 0 ? "text-red-600" : "text-green-600"
                  )} />
                </div>
                <div>
                  <div className="flex items-baseline gap-2">
                    <p className={cn(
                      "text-2xl font-bold",
                      stats.slaBreached > 0 ? "text-red-600" : "text-foreground"
                    )}>{stats.slaBreached}</p>
                    {stats.slaAtRisk > 0 && (
                      <span className="text-sm text-amber-600">+{stats.slaAtRisk} at risk</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">SLA Breached</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              placeholder="Search tickets by ID, subject, or requester..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              aria-label="Search tickets"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-36" aria-label="Filter by category">
              <Tag className="w-4 h-4 mr-2" aria-hidden="true" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-40" aria-label="Filter by status">
              <Circle className="w-4 h-4 mr-2" aria-hidden="true" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statuses.map(status => (
                <SelectItem key={status} value={status} className="capitalize">
                  {status === 'all' ? 'All Statuses' : statusConfig[status]?.label || status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedPriority} onValueChange={setSelectedPriority}>
            <SelectTrigger className="w-36" aria-label="Filter by priority">
              <AlertCircle className="w-4 h-4 mr-2" aria-hidden="true" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="w-4 h-4 mr-1" aria-hidden="true" />
              Clear
            </Button>
          )}
        </div>

        {selectedTickets.size > 0 && (
          <div className="flex items-center gap-4 mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <span className="text-sm font-medium text-primary">
              {selectedTickets.size} ticket{selectedTickets.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={handleBulkAssign}>
                <UserPlus className="w-4 h-4 mr-2" />
                Assign
              </Button>
              <Button variant="outline" size="sm" onClick={handleBulkClose}>
                <Archive className="w-4 h-4 mr-2" />
                Close
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedTickets(new Set())}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        <Card>
          <CardContent className="p-0">
            {paginatedTickets.length > 0 && (
              <div className="flex items-center gap-3 px-4 py-3 border-b bg-muted/30">
                <Checkbox
                  checked={selectedTickets.size > 0 && (selectAllMatching || selectedTickets.size === paginatedTickets.length)}
                  onCheckedChange={handleSelectAllPage}
                  aria-label="Select all tickets on this page"
                />
                <span className="text-xs text-muted-foreground">
                  {selectAllMatching ? (
                    <span className="font-medium text-primary">All {filteredTickets.length} matching selected</span>
                  ) : selectedTickets.size === paginatedTickets.length && paginatedTickets.length > 0 && filteredTickets.length > paginatedTickets.length ? (
                    <span>
                      {paginatedTickets.length} on page selected.{' '}
                      <button
                        onClick={handleSelectAllMatching}
                        className="text-primary hover:underline font-medium"
                      >
                        Select all {filteredTickets.length} matching
                      </button>
                    </span>
                  ) : (
                    <>
                      {filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}
                      {hasFilters && ' (filtered)'}
                    </>
                  )}
                </span>
              </div>
            )}
            <div className="divide-y" role="list" aria-label="Tickets list">
              {paginatedTickets.length === 0 ? (
                <div className="p-12 text-center">
                  <Inbox className="w-12 h-12 text-muted-foreground mx-auto mb-4" aria-hidden="true" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No tickets found</h3>
                  <p className="text-sm text-muted-foreground mb-4">Try adjusting your filters</p>
                  <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
                </div>
              ) : (
                paginatedTickets.map((ticket) => {
                  const status = statusConfig[ticket.status];
                  const priority = priorityConfig[ticket.priority];
                  const StatusIcon = status.icon;
                  const ChannelIcon = channelIcons[ticket.channel.type] || MessageSquare;
                  const isSelected = selectedTickets.has(ticket.id);

                  return (
                    <div
                      key={ticket.id}
                      className={cn(
                        "p-4 hover:bg-muted/30 cursor-pointer transition-colors",
                        isSelected && "bg-primary/5"
                      )}
                      onClick={() => navigate(`/tickets/${ticket.id}`)}
                      role="listitem"
                      aria-label={`Ticket ${ticket.id}: ${ticket.subject}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(e) => handleSelectTicket(ticket.id, { stopPropagation: () => {} })}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select ticket ${ticket.id}`}
                          />
                          {visibleColumns.requester && (
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-xs font-medium shrink-0">
                              {ticket.requester.avatar}
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {visibleColumns.id && (
                              <span className="text-xs font-mono text-muted-foreground">{ticket.id}</span>
                            )}
                            {visibleColumns.priority && (
                              <Badge variant="outline" className={cn("text-xs", priority.color)}>
                                {priority.label}
                              </Badge>
                            )}
                            {visibleColumns.status && (
                              <Badge variant="outline" className={cn("text-xs", status.color)}>
                                <StatusIcon className="w-3 h-3 mr-1" aria-hidden="true" />
                                {status.label}
                              </Badge>
                            )}
                          </div>
                          {visibleColumns.subject && (
                            <h3 className="text-sm font-medium text-foreground truncate">{ticket.subject}</h3>
                          )}
                          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                            {visibleColumns.requester && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" aria-hidden="true" />
                                {ticket.requester.name}
                              </span>
                            )}
                            {visibleColumns.channel && (
                              <span className="flex items-center gap-1">
                                <ChannelIcon className="w-3 h-3" aria-hidden="true" />
                                {ticket.channel.name}
                              </span>
                            )}
                            {visibleColumns.lastActivity && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" aria-hidden="true" />
                                {ticket.lastActivity}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" aria-hidden="true" />
                              {ticket.messageCount}
                            </span>
                          </div>
                          {visibleColumns.aiSuggestion && ticket.aiSuggestion && (
                            <div className="mt-2 flex items-center gap-2 text-xs">
                              <Zap className="w-3 h-3 text-primary" aria-hidden="true" />
                              <span className="text-primary">{ticket.aiSuggestion}</span>
                            </div>
                          )}
                        </div>

                        {visibleColumns.assignedTo && (
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">Assigned to</p>
                              <div className="flex items-center gap-1 text-sm">
                                {ticket.assignedTo.type === 'team' && <UsersIcon className="w-3 h-3" aria-hidden="true" />}
                                {ticket.assignedTo.type === 'agent' && <User className="w-3 h-3" aria-hidden="true" />}
                                {ticket.assignedTo.type === 'workflow' && <Zap className="w-3 h-3" aria-hidden="true" />}
                                <span className="font-medium text-foreground">{ticket.assignedTo.name}</span>
                              </div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>

          {filteredTickets.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(val) => {
                    setPageSize(Number(val));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-20 h-8" aria-label="Rows per page">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map(size => (
                      <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">
                  {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredTickets.length)} of {filteredTickets.length}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    aria-label="Next page"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Tickets;
